import { useMemo, useState, useEffect } from 'react';
import {
  Box, Typography, TextField, IconButton, Switch, Chip, Button, Tooltip, Paper,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  MenuItem, CircularProgress,
} from '@mui/material';
import {
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  RestartAlt as RestoreIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useOrganizationCountry from '../../hooks/useOrganizationCountry';
import { BackgroundCheckCostInfo, useCheckPoints } from './backgroundCheckPricing';

/**
 * Editor del flujo de captación (Parte 1) de una vacante masiva.
 * Las tarjetas siguen el ORDEN REAL del flujo de captación: Contacto e
 * interés → Datos → Revisión de antecedentes → Reglas del cargo → Cierre.
 *
 * FUENTE ÚNICA DE VERDAD: los defaults (preguntas base, mensajes, claves
 * fijas y campos mapeables) se leen del backend vía
 * GET /captation/flow-defaults — aquí no hay nada hardcodeado.
 *
 * Mapeo de respuestas a campos:
 *  - Preguntas con mapeo de sistema (nombre, documento, teléfono, email,
 *    nacionalidad, renta): etiqueta de solo lectura "→ Campo".
 *  - Todas las demás (base sin mapeo y agregadas): selector "Guardar en
 *    campo…" con el catálogo whitelisteado por el backend.
 *
 * El valor emitido se guarda en positions.captacion.flow; el backend hace
 * merge con los defaults y aplica las reglas duras.
 *
 * La consulta de antecedentes (Chapi) ya no es un paso del bot: se hace en
 * la etapa "Verificación de antecedentes" del pipeline (PLAN_MASIVO_PORTAL,
 * fase 1). La tarjeta 3 solo lo informa.
 */

// Estilos compartidos con AtsPipelineConfigurator
const cardSx = { border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' };
const cardHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2,
  py: 1.5,
  backgroundColor: '#EDE9FE',
};
const cardTitleSx = { color: '#4C1D95' };
const rowSx = { border: '1px solid #E5E7EB', borderRadius: 1.5, overflow: 'hidden' };
const rowInnerSx = { display: 'flex', alignItems: 'center', px: 1.5, py: 1, gap: 1 };
const treePrefixSx = { color: '#9CA3AF', fontSize: '0.75rem' };

const CardShell = ({ title, action, children }) => (
  <Paper elevation={0} sx={cardSx}>
    <Box sx={cardHeaderSx}>
      <Typography variant="body2" fontWeight={700} sx={cardTitleSx}>{title}</Typography>
      {action || null}
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Paper>
);

const AtsCaptationFlowEditor = ({ value, onChange, rules, onRulesChange, filteringQuestions = [] }) => {
  const { t } = useTranslation();
  const te = (k, opts) => t(`ats.positions.wizard.captacionBot.editor.${k}`, opts);
  // Antecedentes y documentos de extranjeros: solo organizaciones de Chile
  // (fuera de Chile en espera, PLAN_MASIVO_PORTAL)
  const { operatesInChile, loading: orgCountryLoading } = useOrganizationCountry();
  const backgroundAvailable = orgCountryLoading || operatesInChile;
  // Precio de la verificación de antecedentes (2026-11-01, PLAN_COBRO_ANTECEDENTES)
  const { points: checkPoints } = useCheckPoints();

  // Defaults del sistema, desde el backend (fuente única)
  const [defaults, setDefaults] = useState(null);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get('/captation/flow-defaults');
        const data = response.data.response?.data;
        if (!cancelled && response.data.response?.status_response === 'OK' && data) {
          setDefaults(data);
        } else if (!cancelled) {
          setLoadError(true);
        }
      } catch (err) {
        console.error('Error cargando defaults de captación:', err);
        if (!cancelled) setLoadError(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const defaultQuestions = defaults?.questions || [];
  const defaultMessages = useMemo(() => defaults?.messages || {}, [defaults]);
  const lockedKeys = defaults?.lockedKeys || [];
  const mappableFields = defaults?.mappableFields || [];

  const questions = useMemo(() => {
    if (!defaults) return [];
    if (Array.isArray(value?.questions) && value.questions.length > 0) {
      const keys = new Set(value.questions.map((q) => q.key));
      return [
        ...value.questions.map((q) => ({ ...q })),
        ...defaultQuestions.filter((d) => !keys.has(d.key)).map((d) => ({ key: d.key, text: d.text, enabled: true })),
      ];
    }
    return defaultQuestions.map((d) => ({ key: d.key, text: d.text, enabled: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaults]);

  const messages = useMemo(() => ({ ...defaultMessages, ...(value?.messages || {}) }), [defaultMessages, value]);
  const optInBaseGeneral = value?.optInBaseGeneral !== false;

  const emit = (patch) => onChange({ questions, messages, optInBaseGeneral, extranjeroDocs: value?.extranjeroDocs === true, ...patch });

  // ── Reglas del cargo (F2): opt-in por vacante, deterministas en el backend.
  // Se guardan en positions.captacion.rules (separado del flow). ──
  const rulesEnabled = rules?.enabled === true;
  const emitRules = (patch) => onRulesChange && onRulesChange({ ...(rules || {}), ...patch });
  const ruleAction = (rule, fallback) => rules?.[rule]?.action || fallback;
  const RULE_ACTIONS = ['nada', 'marcar', 'descartar'];
  // Solo yes_no / multiple_choice se pueden comparar de forma determinista
  const eligibleFiltering = (filteringQuestions || []).filter(
    (fq) => fq && fq.id && (fq.type === 'yes_no' || fq.type === 'multiple_choice')
  );
  const fqOptions = (fq) => (fq.type === 'yes_no' ? ['Sí', 'No'] : (fq.options || []));
  const fqRule = (id) => rules?.filtering?.[id] || {};
  const expectedOf = (fq) => (Array.isArray(fqRule(fq.id).expected) ? fqRule(fq.id).expected : []);
  const emitFqRule = (id, patch) => emitRules({
    filtering: { ...(rules?.filtering || {}), [id]: { ...fqRule(id), ...patch } },
  });

  const actionSelect = (valueAction, onChangeAction) => (
    <TextField
      select
      size="small"
      value={valueAction}
      onChange={(e) => onChangeAction(e.target.value)}
      label={te('rules.actionLabel')}
      sx={{
        minWidth: 140,
        '& .MuiOutlinedInput-root': { borderRadius: 999 },
        '& .MuiOutlinedInput-input': { fontSize: '0.8rem', py: 0.75 },
      }}
    >
      {RULE_ACTIONS.map((a) => (
        <MenuItem key={a} value={a}>{te(`rules.actions.${a}`)}</MenuItem>
      ))}
    </TextField>
  );

  const isLocked = (key) => lockedKeys.includes(key);
  const defaultOf = (key) => defaultQuestions.find((d) => d.key === key) || null;
  const isCustom = (key) => !defaultOf(key);
  // Mapeo de sistema: la pregunta base ya define su campo (no editable)
  const systemField = (key) => defaultOf(key)?.applicantField || null;

  const updateQuestion = (index, patch) => {
    emit({ questions: questions.map((q, i) => (i === index ? { ...q, ...patch } : q)) });
  };

  const moveQuestion = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    // nacionalidad siempre antes que RUT (la pregunta del documento depende de ella)
    const iNac = next.findIndex((q) => q.key === 'nacionalidad');
    const iRut = next.findIndex((q) => q.key === 'rut');
    if (iNac > iRut) return;
    emit({ questions: next });
  };

  const removeQuestion = (index) => emit({ questions: questions.filter((_, i) => i !== index) });

  const addQuestion = () => {
    const key = `custom_${(window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2))}`;
    emit({ questions: [...questions, { key, text: '', enabled: true }] });
  };

  const messageField = (mk) => (
    <TextField
      key={mk}
      size="small"
      fullWidth
      multiline
      minRows={2}
      label={te(`messageLabels.${mk}`)}
      value={messages[mk] || ''}
      onChange={(e) => emit({ messages: { ...messages, [mk]: e.target.value } })}
    />
  );

  // info: ícono (i) con un tooltip junto al texto (p. ej. cómo se cobra)
  const statusRow = (label, chipLabel, chipColor, info = null) => (
    <Paper elevation={0} sx={rowSx}>
      <Box sx={rowInnerSx}>
        <Typography sx={treePrefixSx}>└─</Typography>
        <Typography variant="body2" fontWeight={500} sx={{ flexGrow: 1 }}>{label}{info}</Typography>
        <Chip size="small" label={chipLabel} color={chipColor} variant="outlined" />
      </Box>
    </Paper>
  );

  if (loadError) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 2 }}>
        {te('loadError')}
      </Typography>
    );
  }

  if (!defaults) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ── 1 · Contacto e interés ── */}
      <CardShell title={te('contactTitle')}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {messageField('bienvenida')}
          {messageField('interes')}
          <Paper elevation={0} sx={rowSx}>
            <Box sx={rowInnerSx}>
              <Typography sx={treePrefixSx}>└─</Typography>
              <Switch
                size="small"
                checked={optInBaseGeneral}
                onChange={(e) => emit({ optInBaseGeneral: e.target.checked })}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>{te('optInLabel')}</Typography>
                <Typography variant="caption" color="text.secondary">{te('optInHint')}</Typography>
              </Box>
            </Box>
            {optInBaseGeneral && (
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                {messageField('optIn')}
              </Box>
            )}
          </Paper>
        </Box>
      </CardShell>

      {/* ── 2 · Datos del candidato ── */}
      <CardShell
        title={te('dataTitle')}
        action={(
          <Tooltip title={te('restoreFlowHint')} arrow>
            <Button
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => setConfirmRestoreOpen(true)}
            >
              {te('restoreFlow')}
            </Button>
          </Tooltip>
        )}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {te('questionsHint')}
        </Typography>

        <Box sx={{ mb: 2 }}>
          {messageField('inicioPreguntas')}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {questions.map((q, index) => (
            <Paper key={q.key} elevation={0} sx={rowSx}>
              <Box sx={rowInnerSx}>
                <Typography sx={treePrefixSx}>└─</Typography>
                <Switch
                  size="small"
                  checked={isLocked(q.key) ? true : q.enabled !== false}
                  disabled={isLocked(q.key)}
                  onChange={(e) => updateQuestion(index, { enabled: e.target.checked })}
                />
                <TextField
                  size="small"
                  fullWidth
                  variant="standard"
                  value={q.text}
                  placeholder={te('newQuestionPlaceholder')}
                  onChange={(e) => updateQuestion(index, { text: e.target.value })}
                  disabled={q.enabled === false && !isLocked(q.key)}
                  InputProps={{ sx: { fontSize: '0.875rem' } }}
                />

                {/* Mapeo: mismo select en todas las filas; en las de sistema
                    va deshabilitado con su campo fijo (el disabled ya comunica
                    que no se puede cambiar — sin chips extra) */}
                <Tooltip title={systemField(q.key) ? te('systemFieldTooltip') : ''}>
                  <TextField
                    select
                    size="small"
                    value={systemField(q.key) || (mappableFields.includes(q.field) ? q.field : '')}
                    onChange={(e) => updateQuestion(index, { field: e.target.value || undefined })}
                    disabled={Boolean(systemField(q.key))}
                    sx={{
                      minWidth: 240,
                      '& .MuiOutlinedInput-root': { borderRadius: 999 },
                      '& .MuiOutlinedInput-input': { fontSize: '0.8rem', py: 0.75 },
                    }}
                    label={te('fieldLabel')}
                  >
                    {systemField(q.key) ? (
                      <MenuItem value={systemField(q.key)}>
                        {te(`fieldOptions.${systemField(q.key)}`)}
                      </MenuItem>
                    ) : ([
                      <MenuItem key="none" value="" sx={{ color: 'text.disabled' }}>{te('fieldNone')}</MenuItem>,
                      ...mappableFields.map((f) => (
                        <MenuItem key={f} value={f}>{te(`fieldOptions.${f}`)}</MenuItem>
                      )),
                    ])}
                  </TextField>
                </Tooltip>
                <IconButton size="small" onClick={() => moveQuestion(index, -1)} disabled={index === 0}>
                  <UpIcon fontSize="inherit" />
                </IconButton>
                <IconButton size="small" onClick={() => moveQuestion(index, 1)} disabled={index === questions.length - 1}>
                  <DownIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => removeQuestion(index)}
                  disabled={!isCustom(q.key)}
                  sx={{ visibility: isCustom(q.key) ? 'visible' : 'hidden' }}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Box>
              {q.key === 'rut' && (
                <Box sx={{ px: 4, pb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">
                    {te('rutAdaptNote')}
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Box>

        <Button size="small" startIcon={<AddIcon />} onClick={addQuestion} sx={{ mt: 1.5 }}>
          {te('addQuestion')}
        </Button>
      </CardShell>

      {/* ── 3 · Revisión de antecedentes ── */}
      <CardShell title={te('backgroundTitle')}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {statusRow(te('backgroundRutRow'), te('activeChip'), 'success')}
          {backgroundAvailable
            ? statusRow(te('backgroundStageRow', { points: checkPoints }), te('backgroundStageChip'), 'default', <BackgroundCheckCostInfo />)
            : statusRow(te('backgroundStageRowUnavailable'), te('unavailableChip'), 'default')}
          <Paper elevation={0} sx={rowSx}>
            <Box sx={rowInnerSx}>
              <Typography sx={treePrefixSx}>└─</Typography>
              <Switch
                size="small"
                checked={backgroundAvailable && value?.extranjeroDocs === true}
                disabled={!backgroundAvailable}
                onChange={(e) => emit({ extranjeroDocs: e.target.checked })}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>{te('extranjeroDocsLabel')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {backgroundAvailable ? te('extranjeroDocsHint') : te('extranjeroDocsUnavailableHint')}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </CardShell>

      {/* ── 4 · Reglas del cargo (auto-marcado / auto-descarte) ── */}
      <CardShell
        title={te('rulesTitle')}
        action={(
          <Switch
            size="small"
            checked={rulesEnabled}
            onChange={(e) => emitRules({ enabled: e.target.checked })}
          />
        )}
      >
        {!rulesEnabled ? (
          <Typography variant="caption" color="text.secondary">{te('rules.disabledHint')}</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {te('rules.hint')}
            </Typography>

            {/* Renta: marca por defecto; descartar debe elegirse explícitamente */}
            <Paper elevation={0} sx={rowSx}>
              <Box sx={rowInnerSx}>
                <Typography sx={treePrefixSx}>└─</Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>{te('rules.rentaLabel')}</Typography>
                  <Typography variant="caption" color="text.secondary">{te('rules.rentaHint')}</Typography>
                </Box>
                <TextField
                  size="small"
                  type="number"
                  label={te('rules.rentaTope')}
                  value={rules?.renta?.tope ?? ''}
                  onChange={(e) => emitRules({
                    renta: { ...(rules?.renta || {}), tope: e.target.value === '' ? null : Number(e.target.value) },
                  })}
                  sx={{ width: 170, '& .MuiOutlinedInput-input': { fontSize: '0.8rem', py: 0.75 } }}
                />
                {actionSelect(ruleAction('renta', 'marcar'), (a) => emitRules({ renta: { ...(rules?.renta || {}), action: a } }))}
              </Box>
            </Paper>

            {/* Comuna: consciente de modalidad y de la respuesta de traslado */}
            <Paper elevation={0} sx={rowSx}>
              <Box sx={rowInnerSx}>
                <Typography sx={treePrefixSx}>└─</Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>{te('rules.comunaLabel')}</Typography>
                  <Typography variant="caption" color="text.secondary">{te('rules.comunaHint')}</Typography>
                </Box>
                {actionSelect(ruleAction('comuna', 'nada'), (a) => emitRules({ comuna: { ...(rules?.comuna || {}), action: a } }))}
              </Box>
            </Paper>

            {/* Preguntas filtradoras comparables (Sí/No y alternativas) */}
            {eligibleFiltering.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 1.5 }}>
                {te('rules.noFiltering')}
              </Typography>
            ) : (
              eligibleFiltering.map((fq) => (
                <Paper key={fq.id} elevation={0} sx={rowSx}>
                  <Box sx={rowInnerSx}>
                    <Typography sx={treePrefixSx}>└─</Typography>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>{fq.question}</Typography>
                      <Typography variant="caption" color="text.secondary">{te('rules.filteringHint')}</Typography>
                    </Box>
                    {/* Sí/No: una sola respuesta esperada (la lista se cierra al
                        elegir); alternativas: varias. Se guarda siempre como
                        lista (Erwin, 2026-10-08) */}
                    <TextField
                      select
                      size="small"
                      label={te('rules.expectedLabel')}
                      value={fq.type === 'yes_no'
                        ? (expectedOf(fq).length === 1 ? expectedOf(fq)[0] : '')
                        : expectedOf(fq)}
                      onChange={(e) => emitFqRule(fq.id, {
                        expected: fq.type === 'yes_no'
                          ? [e.target.value]
                          : (typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value),
                      })}
                      SelectProps={fq.type === 'yes_no' ? undefined : { multiple: true, renderValue: (sel) => sel.join(' / ') }}
                      sx={{
                        minWidth: 180,
                        '& .MuiOutlinedInput-root': { borderRadius: 999 },
                        '& .MuiOutlinedInput-input': { fontSize: '0.8rem', py: 0.75 },
                      }}
                    >
                      {fqOptions(fq).map((o) => (
                        <MenuItem key={o} value={o}>{o}</MenuItem>
                      ))}
                    </TextField>
                    {actionSelect(fqRule(fq.id).action || 'nada', (a) => emitFqRule(fq.id, { action: a }))}
                  </Box>
                </Paper>
              ))
            )}

            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, mt: 0.5 }}>
              {te('rules.discardNote')}
            </Typography>
          </Box>
        )}
      </CardShell>

      {/* ── 5 · Cierre ── */}
      <CardShell
        title={te('closingTitle')}
        action={(
          <Button size="small" startIcon={<RestoreIcon />} onClick={() => emit({ messages: { ...defaultMessages } })}>
            {te('restoreDefaults')}
          </Button>
        )}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {te('messagesHint')}
        </Typography>
        {messageField('cierreCaptado')}
      </CardShell>

      {/* Confirmación de restaurar flujo (Dialog de la app, no alert nativo) */}
      <Dialog open={confirmRestoreOpen} onClose={() => setConfirmRestoreOpen(false)} maxWidth="xs">
        <DialogTitle>{te('restoreFlow')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{te('restoreFlowConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreOpen(false)}>
            {te('restoreFlowCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Volver al flujo estándar: se eliminan TODOS los overrides de
              // la vacante (preguntas, textos y mensajes)
              onChange(null);
              setConfirmRestoreOpen(false);
            }}
          >
            {te('restoreFlowGo')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtsCaptationFlowEditor;
